import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import {
  addInstance,
  clearDrawPoints,
  clearPending,
  duplicateInstance,
  popDrawPoint,
  removeInstances,
  selectInstance,
  setDrawActive,
  setPortEditInstance,
} from '../features/equipment/equipmentSlice';
import { translatePolygon } from '../utils/equipmentGeometry';
import { cloneEquipmentInstance } from '../features/equipment/equipmentNaming';
import { hasTextSelection } from '../utils/textSelection';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { deleteSelectedLayoutElements } from '../utils/deleteCanvasSelection';

// Session clipboard for equipment (survives re-renders).
let equipmentClipboard: EquipmentInstance | null = null;

/**
 * Keyboard shortcuts scoped to the Equipment Identification tool. Runs on the
 * capture phase so that, when the equipment layer is the active context, it can
 * pre-empt the global layout shortcuts (Delete / Ctrl+D …) without coupling the
 * two features. Undo/redo are excluded: they are global and handled once in
 * useKeyboardShortcuts.
 */
export const useEquipmentShortcuts = () => {
  const dispatch = useAppDispatch();
  const selectedId = useAppSelector((s) => s.equipment.selectedInstanceId);
  const selectedIds = useAppSelector((s) => s.equipment.selectedInstanceIds);
  const selectedLayoutIds = useAppSelector((s) => s.selection.selectedIds);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const portEditId = useAppSelector((s) => s.equipment.portEditInstanceId);
  const draw = useAppSelector((s) => s.equipment.draw);
  const instances = useAppSelector((s) => s.equipment.instances);
  const templates = useAppSelector((s) => s.equipment.templates);
  const rightDrawerTab = useAppSelector((s) => s.ui.rightDrawerTab);

  useEffect(() => {
    const isEquipmentContext =
      !!selectedId || draw.active || !!portEditId || rightDrawerTab === 'equipment';

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const stop = () => {
        e.preventDefault();
        e.stopImmediatePropagation();
      };
      const isCtrl = e.ctrlKey || e.metaKey;

      // Escape: unwind the current interaction step-by-step.
      if (e.key === 'Escape') {
        if (draw.pending) {
          dispatch(clearPending());
          stop();
        } else if (draw.points.length > 0) {
          dispatch(clearDrawPoints());
          stop();
        } else if (draw.active) {
          dispatch(setDrawActive(false));
          stop();
        } else if (portEditId) {
          dispatch(setPortEditInstance(null));
          stop();
        } else if (selectedId) {
          dispatch(selectInstance(null));
          stop();
        }
        return;
      }

      // Backspace while drawing removes the last vertex.
      if (e.key === 'Backspace' && draw.active && draw.points.length > 0) {
        dispatch(popDrawPoint());
        stop();
        return;
      }

      // This capture-phase handler owns equipment deletion. When the marquee
      // also selected layout elements, delete those here too because the global
      // layout shortcut will not receive this event after stop().
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0 && !draw.active) {
        dispatch(removeInstances(selectedIds));
        deleteSelectedLayoutElements(dispatch, selectedLayoutIds, layoutElements);
        stop();
        return;
      }

      // Duplicate: Ctrl+D
      if (isCtrl && e.key.toLowerCase() === 'd' && selectedId) {
        dispatch(duplicateInstance(selectedId));
        stop();
        return;
      }

      // Copy: Ctrl+C. Highlighted text keeps the browser's own copy.
      if (isCtrl && e.key.toLowerCase() === 'c' && selectedId && !hasTextSelection()) {
        const inst = instances[selectedId];
        if (inst) equipmentClipboard = JSON.parse(JSON.stringify(inst));
        stop();
        return;
      }

      // Paste: Ctrl+V
      if (isCtrl && e.key.toLowerCase() === 'v' && equipmentClipboard && isEquipmentContext) {
        const src = equipmentClipboard;
        const templatePorts =
          src.templateId && templates[src.templateId] ? templates[src.templateId].ports : [];
        const copy = cloneEquipmentInstance(src, instances, templatePorts, {
          polygon: translatePolygon(src.polygon, 16, 16),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        dispatch(addInstance(copy));
        dispatch((d, getState) => {
          const order = getState().equipment.instanceOrder;
          const id = order[order.length - 1];
          if (id) d(selectInstance(id));
        });
        stop();
        return;
      }

      // Undo / redo are deliberately NOT handled here. They span both the layout
      // and equipment stacks, so useKeyboardShortcuts dispatches the unified
      // thunks (see features/history/unifiedHistory) for every layer.
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [
    dispatch,
    selectedId,
    selectedIds,
    selectedLayoutIds,
    layoutElements,
    portEditId,
    draw.active,
    draw.pending,
    draw.points.length,
    instances,
    templates,
    rightDrawerTab,
  ]);
};
